<template>
  <div class="sc-container-section">
    <div class="sc-row" role="header">      
      <div class="sc-container-section-header sc-section-header">
        Disbursements
      </div>
      <PermissionsWrapper
        v-if="activeTab === 'table' && hasDirty"
        :disabled="!certification.isEditable"
        :showDisabledIfNotVisible="true"
        :permissionIds="[Permissions.CertAndDisburse]"
        :permissionLevel="PermissionLevels.Update"
      >
        <div class="sc-form-buttons">
          <button class="sc-button cancel" @click="onCancelAll" :disabled="saving">Cancel</button>
          <button class="sc-button save" @click="onSaveAll" :disabled="saving">
            {{ saving ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </PermissionsWrapper>
    </div>
    <div class="sc-row">
      <div class="container">
        <div class="tabs-container scrollbar-container">
          <button
            v-for="tab in tabs"
            :key="tab.identifier"
            :class="{ 'tab-button': true, active: activeTab === tab.identifier }"
            @click="selectTab(tab.identifier)"
          >
            {{ tab.name }}
          </button>
        </div>      
        <component 
          :is="activeTabComponent" 
          v-bind="activeTabProps" 
          class="tab-content"
          ref="tableRef"
        />        
      </div>
    </div>		
  </div>
</template>

<script setup lang="ts">
	import '@/assets/css/scrollbar.css';
	import { computed, onMounted, ref, nextTick, watch, onBeforeUnmount } from 'vue';
  import functions from "@/use/functions";	
  import PermissionsWrapper from "@/components/wrappers/PermissionsWrapper.vue";
  import { Permissions, PermissionLevels } from "@/models/permissions";
  import { useDisbursementsStore } from '@/store/store/disbursementsStore';
  import { CertificationVM } from "@/types/certifications";
  import {
    DisbursementVM,    
  } from "@/types/disbursements";
	import TableTab from '@/components/v2/disbursements/DisbursementsTable.vue';
	import HistoryTab from '@/components/v2/disbursements/TabDisbursementHistory.vue';
	
  const props = defineProps<{
    certification: CertificationVM;
  }>();

  const emit = defineEmits<{
    (e: 'certificationUpdated', value: CertificationVM): void;
  }>();

	interface TabViewModel {
		name: string;
		identifier: string;
	}
	
  const tabs = ref<TabViewModel[]>([
    { name: "Disbursements", identifier: "table" },
    { name: "Disbursement History", identifier: "history" },
  ]);

  const activeTab = ref<string>(tabs.value[0].identifier);
  const hasDirty = ref(false);
  const isLoading = ref<boolean>(true);
  const store = useDisbursementsStore();

  onMounted(() => {
    store.$reset();
    store.setDisbursements(props.certification.disbursements || []);    
    isLoading.value = false;
  });

  onBeforeUnmount(() => {    
    store.$reset();
  });

  watch(
    () => props.certification.form.id,
    (newCertId, oldCertId) => {
      if (oldCertId !== undefined && newCertId !== oldCertId) {
        store.$reset();
        store.setDisbursements(props.certification.disbursements || []);        
        activeTab.value = 'table';
        hasDirty.value = false;
        editingPostDisbFromHistory.value = null;
      }
    }
  );
  
  const tabComponents = {
		table: TableTab,
		history: HistoryTab
	};

  const editingPostDisbFromHistory = ref<{
    disbursementId: number;
    postDisbursementChangeAmountLedgerId: number;
  } | null>(null);

  function handleEditPostDisbursementFromHistory(data: {
    disbursementId: number;
    postDisbursementChangeAmountLedgerId: number;
  }) {
    store.setEditRequest(data);
    activeTab.value = 'table';
    editingPostDisbFromHistory.value = data;
  }

  function handleClearEditRequest() {
    store.setEditRequest(null);
    editingPostDisbFromHistory.value = null;
  }

  const tableRef = ref<InstanceType<typeof TableTab> | null>(null); 
  const saving = ref(false);

	const activeTabComponent = computed(() => tabComponents[activeTab.value] || null);

  const activeTabProps = computed(() => {
		switch (activeTab.value) {			
			case 'table':
        return {
          data: props.certification.disbursements,
          certification: props.certification,
          editingPostDisbFromHistory: editingPostDisbFromHistory.value,
          onHasDirtyChanged: (e: { hasDirty: boolean }) => (hasDirty.value = e.hasDirty),          
          onBatchSaveComplete: handleBatchSaveComplete,
          onSaveCancelled: handleSaveCancelled,
          onSaveValidationFailed: handleSaveValidationFailed,
          onSaveFailed: handleSaveFailed,
          onClearEditRequest: handleClearEditRequest,
          isLoading: isLoading.value
        };
      case 'history':
        return {
          certification: props.certification,
          onEditPostDisbursement: handleEditPostDisbursementFromHistory,
        };
			default:        
				return {};
		}
	});
  
  function handleBatchSaveComplete(updatedDisbursements: DisbursementVM[]) {    
    store.setDisbursements(updatedDisbursements);
    const updatedCertification = {
      ...props.certification,
      disbursements: updatedDisbursements
    };
    
    emit('certificationUpdated', updatedCertification);
    
    saving.value = false;       
  }

  function handleSaveCancelled() {
    saving.value = false;
  }

  function handleSaveValidationFailed() {
    saving.value = false;
  }

  function handleSaveFailed() {
    saving.value = false;
  }

  watch(
  () => props.certification.disbursements,
  (newDisbursements) => {    
    if (store.disbursements.length === 0) {
      store.setDisbursements(newDisbursements || []);
      return;
    }    
    if (store.justSaved) {
      store.setDisbursements(newDisbursements || []);
      return;
    }    
    
    if (store.hasDirty || store.editingPostDisbRequest) {
      return;
    }    
    if (newDisbursements.length > 0) {
      store.setDisbursements(newDisbursements || []);
    }
  },
  { deep: true }
);

	function selectTab(tabIdentifier: string) {
    if (tabIdentifier === 'history' && hasDirty.value) {
      functions.openModal({
        title: 'Unsaved Changes',
        description: 'Please save or cancel your changes before switching tabs.',
      });
      return;
    }
		activeTab.value = tabIdentifier;
	}

  async function onSaveAll() {
    if (!tableRef.value?.saveAll) return;
    saving.value = true;
    try {
      await tableRef.value.saveAll();
    } catch (error) {
      saving.value = false;
      functions.openModal({
        title: 'Error',
        description: 'Failed to save changes. Please try again.',
      });
    }    
  }

  function onCancelAll() {
    if (!tableRef.value?.resetAll) return;
    
    functions.openModal({
      title: 'Confirm',
      description: 'Are you sure you want to discard all pending changes?',
      buttons: [
        { title: 'Cancel', onClick: () => functions.closeModal() },
        {
          title: 'Yes, Discard All',
          onClick: () => {
            functions.closeModal();
            nextTick(() => {
              tableRef.value?.resetAll();
            });
          },
        },
      ],
    });
  }
  
  onMounted(async () => {
    if (!tabs.value.some(t => t.identifier === activeTab.value)) {
      activeTab.value = tabs.value[0].identifier;
    }
    await nextTick();
    isLoading.value = false;
  });
</script>
  
<style scoped>
	.container {
		display: flex;
		flex-direction: column;
		min-height: 100vh;		
		font-family: 'Montserrat', arial, sans-serif;
    width: 100%;
	}

	.tabs-container {
		display: flex;
		margin-top: 10px;
		gap: 0.5rem;		
		overflow-x: auto;
		white-space: nowrap;
    width: 100%; 
	}

	@media (max-width: 960px) {
		.container {
			padding-left: 0;
		}

		.tabs-container {
			padding-left: 0.5rem; 
	    border-top: 0.768px solid #CED1D9;
		}
	}

	.tab-button {
		flex-shrink: 0;
		padding: 0.75rem 1.5rem;
		background-color: #ccc;
		color: #555;
		border-top-left-radius: 5px;
		border-top-right-radius: 5px;
		cursor: pointer;
		white-space: nowrap;
		font-weight: 400;
		font-size: 14px;
		border-top: 1px solid transparent;
		border-left: 1px solid transparent;
		border-right: 1px solid transparent;
		border-bottom: none;
		font-family: 'Montserrat', arial, sans-serif;
		transition: background-color 0.3s ease, color 0.3s ease;
	}

	.tab-button:hover {
		background-color: #bbb;
	}

	.tab-button.active {
		background-color: black;
		color: white;
		border-top: 1px solid #ccc;
		border-left: 1px solid #ccc;
		border-right: 1px solid #ccc;
	}

  .tab-button.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

	.tab-content {		
		padding: 1rem 1rem 1.5rem 0rem;
    border-top: 0.768px solid #CED1D9;
    background-color: #f2f3f5;
	}
</style>
<style scoped src="@/assets/css/certifications/certification-form.css"></style>